import React, { useEffect, useState } from "react";
import { DescriptionText, FlexContainer, H1Text, LeftBackButtonText, Spinner } from "../../design";
import BillingHistory from "./components/billinghistory";
import BaseSplitPage from "../../components/splitpage";
import {
  findSubscriptionV2LinkById,
  updateSubscriptionV2Link,
  pauseSubscriptionLink,
  cancelSubscriptionLink,
  resumeSubscriptionLink,
  paymentMethodDelete,
  paymentUpdate,
} from "../../api";
import PaymentMethodList from "./components/paymentmethodlist";
import SubscribedEmail from "./components/subscribedemail";
import CurrentSubscription from "./components/currentsubscription";
import ManageSubscription from "./components/managesubscription";
import Header from "./components/smallscreen/header";
import { SWIREPAY_URL } from "../../appconstants";
import Support from "../../components/support";
import SmallScreenFooter from "../../components/smallscreen/footer";
import getUniquePaymentTypes from "../../utils/getUniquePaymentTypes";
import SuccessModal from "../../components/modal/success";
import PaymentError from "../../components/paymenterror";

type Props = {
  history: {
    push: Function,
  },
}

const Subscription2Link = (props: Props) => {

  const { match: { params: { id } } } = props;
  const [subscriptionLink, setSubscriptionLink] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [account, setAccount] = useState(null);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [isShowSpinner, setShowSpinner] = useState(false);
  const [subscribedEmailFormShow, setSubscribedEmailFormShow] = useState(false);
  const [backButtonUrl, setBackButtonUrl] = useState(SWIREPAY_URL);
  const [isTransactionSuccessModalOpen, setTransactionSuccessModalOpen] = useState(false);
  const [processStatus, setProcessStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [successMessage, setSuccessMessage] = useState('');
  const [pauseMsg, setPauseMsg] = useState('');
  const [subType, setSubType] = useState('');

  const { location: { pathname } } = props;

  useEffect(() => {
    if (id) { getSubscriptionLinkById(id); }
  }, [id]);

  const getSubscriptionLinkById = async (subscriptionLinkId) => {
    if (subscriptionLinkId) {
      setShowSpinner(true);

      try {
        const value = await findSubscriptionV2LinkById(id);
        if (value && value.subscription2 && value.subscription2.status === 'CANCELLED') {
          props.history.push(`${pathname}/expired`);
          return;
        }
        if (value) {
          setSubscriptionLink(value);
          setSubscription(value.subscription2);
          setSubType(value?.subscription2?.type);
          setPaymentMethod(value?.paymentMethods);
          if (!value?.paymentMethods) {
              props.history.push(`${pathname}/payment-method/add`);
          }
          
          setEmailRecipients(value.subscription2 && value.subscription2.emailRecipientList);
          setAccount(value.account);
          setBackButtonUrl(value.redirectUri)
          const paymenttype = (value.subscription2 && value.subscription2.paymentMethodType && value.subscription2.paymentMethodType.length > 0) ? value.subscription2.paymentMethodType[0] : '';
          const uniquePayments = getUniquePaymentTypes(value.account && value.account.supportedRecurringPaymentTypes);
          const paymentSelected = paymenttype ? (uniquePayments && uniquePayments.indexOf(paymenttype)) : 0;
          sessionStorage.setItem('slectedPayment', paymentSelected);
        }
      } catch (reason) {
        props.history.push(`${pathname}/error`);
      } finally {
        setShowSpinner(false)
      }
    }
  };

  const onSubmit = (value, e) => {
    setSubscribedEmailFormShow(true);
    let emails = [];
    if (emailRecipients) emails = [...emails, ...emailRecipients];
    emails = [...emails, value.email];
    setEmailRecipients(emails);
    const data = { emailRecipientList: emails };
    updateSubscription(subscriptionLink.gid, data);
    setSubscribedEmailFormShow(false);
  };

  const deleteSubscribedEmail = email => {
    emailRecipients.splice(emailRecipients.indexOf(email), 1);
    setEmailRecipients([...emailRecipients]);
    const data = { emailRecipientList: emailRecipients };
    updateSubscription(subscriptionLink.gid, data);
  };

  const updateSubscription = async (gid, data) => {
    setShowSpinner(true);
    try {
      const value = await updateSubscriptionV2Link(gid, data);
      if (value) {
        await getSubscriptionLinkById(id)
      }
    } finally {
      setShowSpinner(false)
    }
  };

  const resumeSubscription = async () => {
    setShowSpinner(true);
    try {
      const value = await resumeSubscriptionLink(subscriptionLink?.gid);
      if (value) {
        setSuccessMessage('Subscription Resumed Successfully');
        setPauseMsg('Welcome back! Your plan has been reactivated');
        setTransactionSuccessModalOpen(true);
        setTimeout(() => {
          setTransactionSuccessModalOpen(false);
          getSubscriptionLinkById(id);
        }, 10000);
      }
    } catch (reason) {
      setErrorMessage(reason?.response?.data?.message);
    } finally {
      setShowSpinner(false)
    }
  };

  const cancelSubscription = async () => {
    setShowSpinner(true);
    try {
      const value = await cancelSubscriptionLink(subscriptionLink?.gid);
      if (value) {
        setSuccessMessage('Your subscription has been cancelled');
        setTransactionSuccessModalOpen(true);
        setTimeout(() => {
          setTransactionSuccessModalOpen(false);
          getSubscriptionLinkById(id);
        }, 5000);
      }
    } catch (reason) {
      setErrorMessage(reason?.response?.data?.message);
    } finally {
      setShowSpinner(false)
    }
  };

  const pauseSubscription = async (interval) => {
    setShowSpinner(true);
    try {
      const value = await pauseSubscriptionLink(Number(interval), subscriptionLink?.gid);
      if (value) {
        setSuccessMessage('Subscription Paused Successfully');
        setPauseMsg('Your plan is now on hold. You can resume anytime');
        setTransactionSuccessModalOpen(true);
        setTimeout(() => {
          setTransactionSuccessModalOpen(false);
          getSubscriptionLinkById(id);
        }, 5000);
      }
    } catch (reason) {
      setErrorMessage(reason?.response?.data?.message);
    } finally {
      setShowSpinner(false)
    } 
  };

  const deletePaymentMethod = async (paymentInfo) => {
    setShowSpinner(true);
    try {
      const value = await paymentMethodDelete(paymentInfo?.gid, subscriptionLink?.gid);
      if (value) {
        setSuccessMessage('Payment Method Deleted Successfully');
        setPauseMsg('');
        setTransactionSuccessModalOpen(true);
        setTimeout(() => {
          setTransactionSuccessModalOpen(false);
          getSubscriptionLinkById(id);
        }, 5000);
      }
    } catch (reason) {
      setErrorMessage(reason?.response?.data?.message);
    } finally {
      setShowSpinner(false)
    } 
  };

  const updatePayment = async (paymentInfo) => {
    setShowSpinner(true);
    try {
      const value = await paymentUpdate(paymentInfo?.gid, subscriptionLink?.gid);
      if (value) {
        setSuccessMessage('Default Payment Method Updated Successfully');
        setPauseMsg('');
        setTransactionSuccessModalOpen(true);
        setTimeout(() => {
          setTransactionSuccessModalOpen(false);
          getSubscriptionLinkById(id);
        }, 5000);
      }
    } catch (reason) {
      setErrorMessage(reason?.response?.data?.message);
    } finally {
      setShowSpinner(false)
    } 
  };

  return (
    <BaseSplitPage
      left={
        <>
          <FlexContainer
            flexDirection="column"
            position="relative"
            display="block"
            height="100%"
          >
            <div>
              <H1Text>
                {account && account.name}
              </H1Text>
              <DescriptionText >
                {subscription && subscription.description}
              </DescriptionText>
            </div>
            <div style={{ position: "sticky", top: "85%" }}>
              <Support phone={account && account.support && account.support.phone}
                whiteLabel={account && account.whiteLabel}
                email={account && account.support && account.support.email} />
            </div>
          </FlexContainer>
        </>
      }
      right={
        <>
          <Header 
            account={account} 
            subscription={subscription} 
            cancelUrlLink={null}
          />
          <FlexContainer
            className="pt-3 pt-lg-5 w-100"
            box-shadow="0 0 15px 0 rgba(0, 0, 0, 0.1)"
            display="flow-root"
          >
            <CurrentSubscription
              subscription={subscription}
              account={account}
              history={props.history}
            />
            <PaymentMethodList
              paymentMethod={paymentMethod}
              account={account}
              history={props.history}
              deletePaymentMethod={deletePaymentMethod}
              subType={subType}
              updatePayment={updatePayment}
              savedPaymentsLimit={subscription?.savedPaymentsLimit || 1}
            />
            <ManageSubscription
              pauseSubscription={pauseSubscription}
              cancelSubscription={cancelSubscription}
              subscription={subscription}
              resumeSubscription={resumeSubscription}
              pathname={pathname}
              history={props.history}
              subType={subType}
              allowCustomerPauseOrCancel={subscription?.allowCustomerPauseOrCancel}
            />
            <SubscribedEmail emailRecipients={emailRecipients} add={onSubmit} formShow={subscribedEmailFormShow} remove={deleteSubscribedEmail} />
            <BillingHistory id={id} timeZone={account && account.timezone} />
          </FlexContainer>
          <SmallScreenFooter phone={account && account.support && account.support.phone}
            whiteLabel={account && account.whiteLabel}
            email={account && account.support && account.support.email} children={""} />
          <Spinner isOpen={isShowSpinner} />
          <SuccessModal isOpen={isTransactionSuccessModalOpen} processing={processStatus} accountId={account?.gid} paymentId={id} message={successMessage} pauseType={pauseMsg}/>
          {
            errorMessage && (
              <PaymentError header="Unable to update subscription" message={errorMessage} />
            )
          }
          
        </>
      }
     
    />
  )
};

export default Subscription2Link
